import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Image from "../../../components/AppImage";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";

const RoomImageGallery = ({ images = [], title = "" }) => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const [showSwipeHint, setShowSwipeHint] = useState(false);

  const nextImage = () => setCurrentImageIndex((p) => (p + 1) % images.length);
  const prevImage = () =>
    setCurrentImageIndex((p) => (p - 1 + images.length) % images.length);

  const openLightbox = (i) => {
    setCurrentImageIndex(i);
    setShowLightbox(true);
  };
  const closeLightbox = () => setShowLightbox(false);

  const openViewer = () => {
    navigate("/image-gallery-viewer", { state: { images, title } });
  };

  // ✅ Swipe hint (show once)
  useEffect(() => {
    const key = "apsonja_swipe_hint_seen_room";
    const seen = localStorage.getItem(key);
    if (seen) return;

    setShowSwipeHint(true);
    const t = setTimeout(() => {
      setShowSwipeHint(false);
      localStorage.setItem(key, "1");
    }, 1600);

    return () => clearTimeout(t);
  }, []);

  // ✅ Swipe handlers
  const touchRef = useRef({ x: 0, y: 0 });

  const onTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    touchRef.current = { x: t.clientX, y: t.clientY };
  };

  const onTouchEnd = (e) => {
    const t = e.changedTouches?.[0];
    if (!t) return;

    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;

    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 30) {
      if (dx > 0) prevImage();
      else nextImage();
    }
  };

  // ✅ Keyboard in lightbox (desktop)
  useEffect(() => {
    if (!showLightbox) return;

    const onKeyDown = (e) => {
      if (e.key === "ArrowLeft") prevImage();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "Escape") closeLightbox();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showLightbox, images.length]);

  if (!images?.length) {
    return (
      <div className="h-96 bg-muted rounded-xl flex items-center justify-center">
        <div className="text-center">
          <Icon
            name="Image"
            size={48}
            className="text-muted-foreground mx-auto mb-2"
          />
          <p className="text-muted-foreground">No images available</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="relative">
        {/* MAIN IMAGE */}
        <div
          className="relative h-64 sm:h-80 md:h-[500px] rounded-xl overflow-hidden group bg-muted"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <Image
            src={images[currentImageIndex]}
            alt={`${title} - Image ${currentImageIndex + 1}`}
            className="w-full h-full object-contain md:object-cover cursor-pointer"
            onClick={() => openLightbox(currentImageIndex)}
          />

          {/* Mobile tap zones */}
          {images.length > 1 && (
            <div className="absolute inset-0 flex md:hidden">
              <button
                type="button"
                className="w-1/2 h-full"
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                aria-label="Previous image"
              />
              <button
                type="button"
                className="w-1/2 h-full"
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                aria-label="Next image"
              />
            </div>
          )}

          {/* Mobile hint */}
          {showSwipeHint && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full md:hidden">
              Swipe left/right
            </div>
          )}

          {/* Arrows (always visible on mobile, hover on desktop) */}
          {images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-smooth"
                aria-label="Previous image"
              >
                <Icon name="ChevronLeft" size={20} />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-smooth"
                aria-label="Next image"
              >
                <Icon name="ChevronRight" size={20} />
              </button>
            </>
          )}

          {/* View all photos */}
          <div className="absolute top-4 right-4 flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={openViewer}
              iconName="Images"
            >
              View all photos
            </Button>
            <button
              onClick={() => openLightbox(currentImageIndex)}
              className="bg-black/50 hover:bg-black/70 text-white p-2 rounded-full opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-smooth"
              title="Fullscreen"
              aria-label="Fullscreen"
            >
              <Icon name="Expand" size={16} />
            </button>
          </div>

          <div className="absolute bottom-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
            {currentImageIndex + 1} / {images.length}
          </div>
        </div>

        {/* THUMBS */}
        {images.length > 1 && (
          <div className="mt-4 flex space-x-2 overflow-x-auto pb-2">
            {images.map((image, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-smooth ${
                  index === currentImageIndex
                    ? "border-primary"
                    : "border-transparent hover:border-border"
                }`}
                aria-label={`Open image ${index + 1}`}
              >
                <Image
                  src={image}
                  alt={`${title} - Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* LIGHTBOX */}
      {showLightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={closeLightbox}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div
            className="relative max-w-7xl max-h-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeLightbox}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-smooth"
              aria-label="Close"
            >
              <Icon name="X" size={24} />
            </button>

            <div className="relative">
              <Image
                src={images[currentImageIndex]}
                alt={`${title} - Image ${currentImageIndex + 1}`}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />

              {/* Lightbox tap zones (mobile) */}
              {images.length > 1 && (
                <div className="absolute inset-0 flex md:hidden">
                  <button
                    type="button"
                    className="w-1/2 h-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      prevImage();
                    }}
                    aria-label="Previous image"
                  />
                  <button
                    type="button"
                    className="w-1/2 h-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      nextImage();
                    }}
                    aria-label="Next image"
                  />
                </div>
              )}

              {images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-smooth"
                    aria-label="Previous image"
                  >
                    <Icon name="ChevronLeft" size={20} />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-smooth"
                    aria-label="Next image"
                  >
                    <Icon name="ChevronRight" size={20} />
                  </button>
                </>
              )}

              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full text-sm">
                {currentImageIndex + 1} / {images.length}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RoomImageGallery;
